import React, { useEffect, useState } from "react";
import { FileText, RefreshCw } from "lucide-react";
import { http } from "@/lib/api";

const ENTITY_FILTERS = ["all", "job", "agent", "schedule", "share", "user"];

export default function AuditLog() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");

    const load = async () => {
        setLoading(true);
        try {
            const q = filter === "all" ? "" : `?entity_type=${filter}`;
            const { data } = await http.get(`/audit/logs${q}`);
            setRows(data);
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [filter]);

    const fmt = (iso) => {
        try {
            return new Date(iso).toLocaleString();
        } catch {
            return iso;
        }
    };

    return (
        <div className="mx-auto max-w-[1600px] p-6">
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                        Compliance
                    </div>
                    <h2 className="mt-1 font-display text-2xl font-bold">Audit trail</h2>
                </div>
                <button
                    onClick={load}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:border-foreground/30"
                    data-testid="audit-refresh"
                >
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            <div className="mb-4 flex flex-wrap gap-1" data-testid="audit-filter">
                {ENTITY_FILTERS.map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={
                            "rounded-md border px-3 py-1.5 text-xs font-medium capitalize transition-colors " +
                            (filter === f
                                ? "border-[#5b5ef0] bg-[#5b5ef0]/5 text-[#5b5ef0]"
                                : "border-border hover:border-foreground/30")
                        }
                        data-testid={`audit-filter-${f}`}
                    >
                        {f}
                    </button>
                ))}
            </div>

            <div className="rounded-xl border border-border bg-surface">
                {rows.length === 0 && !loading && (
                    <div className="py-16 text-center text-sm text-muted-foreground">
                        <FileText size={18} className="mx-auto mb-2 opacity-40" />
                        No audit entries match this filter yet.
                    </div>
                )}
                {rows.length > 0 && (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                <th className="px-4 py-2">When</th>
                                <th className="px-4 py-2">Actor</th>
                                <th className="px-4 py-2">Action</th>
                                <th className="px-4 py-2">Entity</th>
                                <th className="px-4 py-2">Metadata</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {rows.map((r) => (
                                <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                                    <td className="px-4 py-2 font-mono text-[11px] text-muted-foreground">
                                        {fmt(r.created_at)}
                                    </td>
                                    <td className="px-4 py-2 text-[12px] font-medium">{r.user_email}</td>
                                    <td className="px-4 py-2">
                                        <span className="rounded bg-[#5b5ef0]/10 px-2 py-0.5 font-mono text-[10px] font-semibold text-[#5b5ef0]">
                                            {r.action}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 font-mono text-[11px]">
                                        <span className="text-muted-foreground">{r.entity_type}</span>
                                        {r.entity_id && (
                                            <span className="text-foreground"> · {r.entity_id.slice(0, 8)}</span>
                                        )}
                                    </td>
                                    <td className="max-w-xs truncate px-4 py-2 font-mono text-[10px] text-muted-foreground">
                                        {Object.keys(r.metadata || {}).length
                                            ? JSON.stringify(r.metadata)
                                            : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
